import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faMagic, 
    faArrowLeft, 
    faDownload, 
    faShareAlt, 
    faChevronLeft, 
    faChevronRight,
    faThLarge,
    faCaretRight,
    faEdit,
    faCopy,
    faTimes,
    faSpinner,
    faCheckCircle,
    faSave
} from '@fortawesome/free-solid-svg-icons';
import { drawOnCanvas } from './canvasUtils';
import QuiltEditor from './QuiltEditor';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, setDoc, getDoc, onSnapshot } from "firebase/firestore";

const Step5 = ({
    aiGeneratedLayouts,
    quiltGrid,
    gridSize,
    logoSrc,
    currentUser,
    storage,
    db,
    handleDownloadClick,
    handleShareClick,
    prevStep,
    setShowModal,
    setShareLink,
    onSelectLayout
}) => {
    // View state
    const [viewMode, setViewMode] = useState('carousel');
    const [currentIndex, setCurrentIndex] = useState(0);
    const currentViewModeRef = useRef('carousel');
    
    // Layout data state
    const [processedLayouts, setProcessedLayouts] = useState([]);
    const [originalLayouts, setOriginalLayouts] = useState([]);
    const [layoutsModified, setLayoutsModified] = useState(false);
    
    // Editing state
    const [isEditing, setIsEditing] = useState(false);
    const [editingLayout, setEditingLayout] = useState(null);
    const [editingIndex, setEditingIndex] = useState(null);
    const [layoutTiles, setLayoutTiles] = useState([]);
    
    // Sharing state
    const [localShowModal, setLocalShowModal] = useState(false);
    const [localShareLink, setLocalShareLink] = useState("");
    const [sharingLayoutIndex, setSharingLayoutIndex] = useState(null);
    
    // Canvas and drawing state
    const [modalClosedTimestamp, setModalClosedTimestamp] = useState(null);
    const [redrawTrigger, setRedrawTrigger] = useState(Date.now());
    
    // Save state
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [saveError, setSaveError] = useState(null);
    
    // Loading state
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    
    // Operation state
    const ongoingOperationRef = useRef(false);
    const shouldSaveRef = useRef(false);
    
    // Update the ref whenever viewMode changes
    useEffect(() => {
        currentViewModeRef.current = viewMode;
    }, [viewMode]);

    // Initial load of layouts - prioritize user_layouts collection
    useEffect(() => {
        const loadLayouts = async () => {
            if (!currentUser?.uid && !db) {
                // Try to use the stored user ID from session storage
                const storedUserId = sessionStorage.getItem('aiProcessingUserId');
                if (!storedUserId) {
                    setIsLoading(false);
                    setLoadError("Authentication required to load your layouts");
                    return;
                }
            }
            
            setIsLoading(true);
            setLoadError(null);
            
            try {
                // Try to determine the user ID - either from props or session storage
                const userId = currentUser?.uid || sessionStorage.getItem('aiProcessingUserId');
                
                if (!userId) {
                    setLoadError("User ID not available");
                    setIsLoading(false);
                    return;
                }
                
                console.log("Loading layouts from user_layouts collection...");
                const layoutsRef = doc(db, "user_layouts", userId);
                const docSnap = await getDoc(layoutsRef);
                
                let loadedLayouts = null;
                
                // First try to get layouts from user_layouts collection
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    
                    // Check for AI layouts
                    if (data.aiLayouts && Array.isArray(data.aiLayouts) && data.aiLayouts.length > 0) {
                        console.log("Found aiLayouts in user_layouts:", data.aiLayouts.length);
                        
                        // Deserialize the layouts
                        const deserializedLayouts = deserializeLayouts(data.aiLayouts, gridSize);
                        loadedLayouts = deserializedLayouts;
                    }
                }
                
                // If no layouts found in user_layouts, check session storage
                if (!loadedLayouts) {
                    try {
                        const sessionLayouts = sessionStorage.getItem('aiGeneratedLayouts');
                        
                        if (sessionLayouts) {
                            const parsedLayouts = JSON.parse(sessionLayouts);
                            
                            if (Array.isArray(parsedLayouts) && parsedLayouts.length > 0) {
                                console.log("Using aiGeneratedLayouts from sessionStorage:", parsedLayouts.length);
                                loadedLayouts = parsedLayouts;
                            }
                        }
                    } catch (error) {
                        console.error("Error parsing sessionStorage layouts:", error);
                    }
                }
                
                // If still no layouts, fall back to provided aiGeneratedLayouts
                if (!loadedLayouts && aiGeneratedLayouts && aiGeneratedLayouts.length > 0) {
                    console.log("Using original aiGeneratedLayouts:", aiGeneratedLayouts.length);
                    loadedLayouts = [...aiGeneratedLayouts];
                }
                
                // Update state with loaded layouts
                if (loadedLayouts && loadedLayouts.length > 0) {
                    console.log("Setting processed and original layouts:", loadedLayouts.length);
                    setProcessedLayouts(loadedLayouts);
                    setOriginalLayouts(JSON.parse(JSON.stringify(loadedLayouts))); // Deep copy
                    setLayoutsModified(false);
                    
                    // Reset editing state
                    setIsEditing(false);
                    setEditingLayout(null);
                    setEditingIndex(null);
                } else {
                    console.log("No layouts found");
                    setLoadError("No layouts found");
                    
                    // Check if there's an ongoing AI processing
                    const aiProcessingStarted = sessionStorage.getItem('aiProcessingStarted');
                    const paymentSuccessful = sessionStorage.getItem('aiPaymentSuccessful');
                    
                    if (aiProcessingStarted === 'true' || paymentSuccessful === 'true') {
                        setLoadError("AI layouts are still being generated. Please wait a moment and refresh the page.");
                    }
                }
            } catch (error) {
                console.error("Error loading layouts:", error);
                // Show user-friendly error if it's an AI/Anthropic error
                if (
                    error.message &&
                    (
                        error.message.includes('Anthropic') ||
                        error.message.includes('Claude') ||
                        error.message.includes('overloaded') ||
                        error.message.includes('429') ||
                        error.message.includes('timeout') ||
                        error.message.includes('rate limit') ||
                        error.message.includes('temporarily unavailable')
                    )
                ) {
                    setLoadError("Our AI service is currently busy or unavailable. Please try again after some time.");
                } else {
                    setLoadError(`Error loading layouts: ${error.message}`);
                }
                
                // Fall back to original layouts if available
                if (aiGeneratedLayouts && aiGeneratedLayouts.length > 0) {
                    console.log("Falling back to original aiGeneratedLayouts after error");
                    setProcessedLayouts([...aiGeneratedLayouts]);
                    setOriginalLayouts(JSON.parse(JSON.stringify(aiGeneratedLayouts))); // Deep copy
                    setLayoutsModified(false);
                }
            } finally {
                setIsLoading(false);
                setRedrawTrigger(Date.now());
            }
        };
        
        loadLayouts();
    }, [currentUser, db, aiGeneratedLayouts, gridSize]);
    
    // Function to deserialize layouts from Firestore format
    const deserializeLayouts = (serializedLayouts, gridSize) => {
        return serializedLayouts.map(layout => {
            const deserializedLayout = {...layout};
            
            // Convert object matrix back to 2D array if needed
            if (deserializedLayout.matrix && !Array.isArray(deserializedLayout.matrix)) {
                // Get grid dimensions
                const layoutGridSize = deserializedLayout.original_grid_size || gridSize;
                const [cols, rows] = layoutGridSize.split('x').map(Number);
                
                // Create empty 2D array
                const matrixArray = Array(rows).fill().map(() => Array(cols).fill(""));
                
                // Fill the array from the object representation
                Object.entries(deserializedLayout.matrix).forEach(([key, value]) => {
                    const [rowIndex, colIndex] = key.split('_').map(Number);
                    if (rowIndex >= 0 && rowIndex < rows && colIndex >= 0 && colIndex < cols) {
                        matrixArray[rowIndex][colIndex] = value;
                    }
                });
                
                // Replace the object matrix with the array representation
                deserializedLayout.matrix = matrixArray;
                
                console.log(`Converted matrix for "${deserializedLayout.layout_name}" from object to 2D array:`, matrixArray);
            } else if (Array.isArray(deserializedLayout.matrix)) {
                console.log(`Matrix for "${deserializedLayout.layout_name}" is already a 2D array`);
            }
            
            return deserializedLayout;
        });
    };
    
    // Function to serialize layouts for Firestore storage
    const serializeLayouts = (layouts) => {
        return layouts.map(layout => {
            const serializedLayout = {...layout};
            
            // Convert 2D array matrix to object format for storage
            if (serializedLayout.matrix && Array.isArray(serializedLayout.matrix)) {
                const matrixObject = {};
                
                serializedLayout.matrix.forEach((row, rowIndex) => {
                    if (Array.isArray(row)) {
                        row.forEach((cell, colIndex) => {
                            matrixObject[`${rowIndex}_${colIndex}`] = cell;
                        });
                    }
                });
                
                // Replace the array matrix with the object representation
                serializedLayout.matrix = matrixObject;
            }
            
            return serializedLayout;
        });
    };

    // Save edited layouts to Firestore - ONLY called explicitly
    const saveLayoutsToFirestore = async () => {
        if (!currentUser?.uid || !processedLayouts || processedLayouts.length === 0) {
            return {
                success: false,
                error: "Missing required data for saving"
            };
        }
        
        setIsSaving(true);
        setSaveError(null);
        
        try {
            // Flag to prevent concurrent operations
            ongoingOperationRef.current = true;
            
            // Create a document reference for user's layouts
            const layoutsRef = doc(db, "user_layouts", currentUser.uid);
            
            // Serialize layouts for storage
            const serializedLayouts = serializeLayouts(processedLayouts);
            
            // Generate a version ID for this update
            const versionId = Date.now().toString();
            
            // Save to Firestore
            await setDoc(layoutsRef, {
                updatedAt: new Date(),
                userId: currentUser.uid,
                aiLayouts: serializedLayouts,
                gridSize,
                type: "ai",
                versionId: versionId,
                lastEdited: new Date().toISOString(),
                quiltGrid: quiltGrid
            }, { merge: true });
            
            console.log("Saved AI layouts to Firestore with version:", versionId);
            
            // Update the original layouts to match the current state
            setOriginalLayouts(JSON.parse(JSON.stringify(processedLayouts)));
            setLayoutsModified(false);
            setSaveSuccess(true);
            
            // Clear success message after delay
            setTimeout(() => {
                setSaveSuccess(false);
            }, 3000);
            
            return { success: true };
        } catch (error) {
            console.error("Error saving AI layouts:", error);
            setSaveError(`Error saving: ${error.message}`);
            
            return {
                success: false,
                error: error.message
            };
        } finally {
            setIsSaving(false);
            ongoingOperationRef.current = false;
        }
    };

    // Function to redraw all canvases
    const redrawAllCanvases = () => {
        console.log("Redrawing all canvases at", new Date().toISOString());
        
        if (!processedLayouts || processedLayouts.length === 0) {
            console.warn("No layouts to redraw");
            return;
        }
        
        // Add a slight delay to ensure DOM is ready
        setTimeout(() => {
            try {
                if (currentViewModeRef.current === 'grid') {
                    // Redraw all grid canvases
                    processedLayouts.forEach((layout, index) => {
                        const canvas = document.getElementById(`grid-canvas-${index}`);
                        if (canvas) {
                            // Force clear canvas before drawing
                            const ctx = canvas.getContext('2d');
                            if (ctx) {
                                const width = canvas.width;
                                const height = canvas.height;
                                ctx.clearRect(0, 0, width, height);
                                
                                // Reset dimensions to force a complete refresh
                                canvas.width = 1;
                                canvas.height = 1;
                                canvas.width = width;
                                canvas.height = height;
                            }
                            
                            // Now redraw with a fresh canvas state
                            drawLayoutCanvas(canvas, layout, index);
                        }
                    });
                } else if (currentViewModeRef.current === 'carousel') {
                    // Redraw just the current carousel canvas
                    const carouselCanvas = document.getElementById('carousel-canvas');
                    if (carouselCanvas && processedLayouts[currentIndex]) {
                        // Force clear canvas before drawing
                        const ctx = carouselCanvas.getContext('2d');
                        if (ctx) {
                            const width = carouselCanvas.width;
                            const height = carouselCanvas.height;
                            ctx.clearRect(0, 0, width, height);
                            
                            // Reset dimensions to force a complete refresh
                            carouselCanvas.width = 1;
                            carouselCanvas.height = 1;
                            carouselCanvas.width = width;
                            carouselCanvas.height = height;
                        }
                        
                        // Now redraw with a fresh canvas state
                        drawLayoutCanvas(carouselCanvas, processedLayouts[currentIndex], currentIndex);
                    }
                }
            } catch (error) {
                console.error("Error during canvas redraw:", error);
            }
        }, 10);
    };

    // Calculate canvas size based on actual tile dimensions from Firestore
    const getCanvasSize = (layoutGridSize, firestoreDimensions) => {
        const sizeToUse = layoutGridSize || gridSize;
        if (!sizeToUse || !sizeToUse.includes('x')) return { width: 600, height: 600 };
        
        const [cols, rows] = sizeToUse.split('x').map(Number);
        
        // Use actual tile dimensions if available from Firestore
        let tileWidth = 100; // default
        let tileHeight = 100; // default
        
        if (firestoreDimensions) {
            tileWidth = firestoreDimensions.tileWidthPixels || 100;
            tileHeight = firestoreDimensions.tileHeightPixels || 100;
        } else {
            // For mobile, use smaller tile size as fallback
            const baseTileSize = window.innerWidth < 640 ? 80 : 100;
            tileWidth = tileHeight = baseTileSize;
        }
        
        const width = cols * tileWidth;
        const height = rows * tileHeight;
        
        // Ensure the canvas isn't too large for mobile screens
        const maxWidth = Math.min(width, window.innerWidth - 40);
        const scaleFactor = maxWidth / width;
        
        return { 
            width: maxWidth, 
            height: Math.round(height * scaleFactor)
        };
    };

    // Navigate through layouts
    const goToPrevLayout = () => {
        if (!processedLayouts || processedLayouts.length === 0) return;
        setCurrentIndex(prev => prev === 0 ? processedLayouts.length - 1 : prev - 1);
    };

    const goToNextLayout = () => {
        if (!processedLayouts || processedLayouts.length === 0) return;
        setCurrentIndex(prev => prev === processedLayouts.length - 1 ? 0 : prev + 1);
    };

    // Prepare tiles for editing a layout
    const prepareLayoutTilesForEditing = (layout, index) => {
        // Create a position map from the original quilt grid
        const tilePositionMap = {};
        quiltGrid.forEach(tile => {
            if (tile && tile.position) {
                const [row, col] = tile.position;
                tilePositionMap[`${row},${col}`] = tile;
            }
        });
        
        // Get the layout-specific grid size
        const layoutGridSize = layout.original_grid_size || gridSize;
        const [cols, rows] = layoutGridSize.split('x').map(Number);
        
        // Convert matrix to drawable tiles
        let tiles = [];
        
        if (Array.isArray(layout.matrix) && Array.isArray(layout.matrix[0])) {
            // Process 2D array matrix
            layout.matrix.forEach((row, rowIndex) => {
                row.forEach((position, colIndex) => {
                    const posKey = typeof position === 'string' ? position : `${position[0]},${position[1]}`;
                    const originalTile = tilePositionMap[posKey];
                    
                    if (originalTile) {
                        tiles.push({
                            ...originalTile,
                            position: [rowIndex, colIndex],
                            originalPosition: [
                                typeof position === 'string' ? 
                                    parseInt(position.split(',')[0]) : 
                                    position[0], 
                                typeof position === 'string' ? 
                                    parseInt(position.split(',')[1]) : 
                                    position[1]
                            ]
                        });
                    }
                });
            });
        } else if (typeof layout.matrix === 'object' && !Array.isArray(layout.matrix)) {
            // Initialize a 2D array matrix from object format
            const matrixArray = Array(rows).fill().map(() => Array(cols).fill(null));
            
            // Fill the matrix array from the object
            Object.entries(layout.matrix).forEach(([key, value]) => {
                const [rowIdx, colIdx] = key.split('_').map(Number);
                if (rowIdx >= 0 && rowIdx < rows && colIdx >= 0 && colIdx < cols) {
                    matrixArray[rowIdx][colIdx] = value;
                }
            });
            
            // Convert matrix array to drawable tiles
            matrixArray.forEach((row, rowIndex) => {
                row.forEach((position, colIndex) => {
                    if (position) {
                        const posKey = typeof position === 'string' ? position : `${position[0]},${position[1]}`;
                        const originalTile = tilePositionMap[posKey];
                        
                        if (originalTile) {
                            tiles.push({
                                ...originalTile,
                                position: [rowIndex, colIndex],
                                originalPosition: [
                                    typeof position === 'string' ? 
                                        parseInt(position.split(',')[0]) : 
                                        position[0], 
                                    typeof position === 'string' ? 
                                        parseInt(position.split(',')[1]) : 
                                        position[1]
                                ]
                            });
                        }
                    }
                });
            });
        }
        
        // Ensure all grid positions are filled
        const existingPositions = new Set(
            tiles.map(tile => `${tile.position[0]},${tile.position[1]}`)
        );
        
        // Find which positions are missing
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const posKey = `${r},${c}`;
                if (!existingPositions.has(posKey)) {
                    // Use the first tile as a template if available
                    if (quiltGrid.length > 0) {
                        const templateTile = quiltGrid[0];
                        tiles.push({
                            ...templateTile,
                            position: [r, c],
                            placeholder: true
                        });
                    }
                }
            }
        }
        
        return tiles;
    };

    // Handle edit button click
    const handleEditClick = (layout, index) => {
        const tiles = prepareLayoutTilesForEditing(layout, index);
        
        setEditingLayout(layout);
        setEditingIndex(index);
        setLayoutTiles(tiles);
        setIsEditing(true);
        
        // Reset save flags when starting edit
        setSaveSuccess(false);
        setSaveError(null);
    };

    // Handle saving changes from editor - updated to automatically save to Firestore after editing
    const handleSaveChanges = async (updatedGrid) => {
        if (editingIndex === null || editingIndex < 0 || editingIndex >= processedLayouts.length) {
            console.error("Invalid editing index:", editingIndex);
            return;
        }
        
        console.log("Saving changes from editor with grid:", updatedGrid);
        
        // Extract the position and originalPosition from updatedGrid
        const positionMappings = {};
        
        // Update the layout tiles with the edited grid
        setLayoutTiles(updatedGrid);
        
        // Create a new matrix from the updated grid
        const layoutGridSize = processedLayouts[editingIndex].original_grid_size || gridSize;
        const [cols, rows] = layoutGridSize.split('x').map(Number);
        const newMatrix = Array(rows).fill().map(() => Array(cols).fill(""));
        
        // First pass: Build a map of [row,col] positions to source positions
        updatedGrid.forEach(tile => {
            if (tile && tile.position && Array.isArray(tile.position)) {
                const [row, col] = tile.position;
                
                // Determine the source position (original quilt grid position)
                let sourcePos;
                if (tile.originalPosition && Array.isArray(tile.originalPosition)) {
                    const [origRow, origCol] = tile.originalPosition;
                    sourcePos = `${origRow},${origCol}`;
                } else {
                    // Fall back to tile URL as identifier if no originalPosition
                    sourcePos = tile.url;
                }
                
                positionMappings[`${row},${col}`] = sourcePos;
            }
        });
        
        // Second pass: Fill in the matrix using the position mappings
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                const posKey = `${row},${col}`;
                if (positionMappings[posKey]) {
                    newMatrix[row][col] = positionMappings[posKey];
                } else {
                    // Use position itself as fallback
                    newMatrix[row][col] = posKey;
                }
            }
        }
        
        // Create a timestamp to force refreshes
        const updateTimestamp = Date.now();
        
        // Create a deep copy of processed layouts to avoid reference issues
        const updatedLayouts = JSON.parse(JSON.stringify(processedLayouts));
        
        // Update the specific layout with the new matrix
        updatedLayouts[editingIndex] = {
            ...updatedLayouts[editingIndex],
            matrix: newMatrix,
            lastUpdated: updateTimestamp,
            forceRender: updateTimestamp
        };
        
        console.log("Updated layout with new matrix:", newMatrix);
        
        // Update layouts state with the new version
        setProcessedLayouts(updatedLayouts);
        
        // Exit edit mode
        setIsEditing(false);
        setEditingLayout(null);
        setEditingIndex(null);
        
        // Show saving indicator
        setIsSaving(true);
        setSaveError(null);
        
        try {
            // Save changes to Firestore automatically
            console.log("Automatically saving changes to Firestore");
            
            // Serialize layouts for storage
            const serializedLayouts = serializeLayouts(updatedLayouts);
            
            // Generate a version ID for this update
            const versionId = Date.now().toString();
            
            // Save to Firestore
            if (currentUser?.uid && db) {
                // Create a document reference for user's layouts
                const layoutsRef = doc(db, "user_layouts", currentUser.uid);
                
                await setDoc(layoutsRef, {
                    updatedAt: new Date(),
                    userId: currentUser.uid,
                    aiLayouts: serializedLayouts,
                    gridSize,
                    type: "ai",
                    versionId: versionId,
                    lastEdited: new Date().toISOString(),
                    quiltGrid: quiltGrid
                }, { merge: true });
                
                console.log("Saved AI layouts to Firestore with version:", versionId);
                
                // Update the original layouts to match the current state
                setOriginalLayouts(JSON.parse(JSON.stringify(updatedLayouts)));
                setLayoutsModified(false);
                setSaveSuccess(true);
                
                // Clear success message after delay
                setTimeout(() => {
                    setSaveSuccess(false);
                }, 3000);
            } else {
                console.error("Missing user or database reference for auto-save");
                setSaveError("Could not save changes: Missing authentication");
            }
        } catch (error) {
            console.error("Error auto-saving AI layouts:", error);
            setSaveError(`Error saving: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
        
        // Force a complete redraw after saving changes with more aggressive approach
        const forceCanvasReset = () => {
            // First, update the redraw trigger to a new timestamp
            setRedrawTrigger(Date.now());
            
            // Helper function to completely reset a canvas
            const resetCanvas = (canvas) => {
                if (!canvas || !canvas.getContext) return;
                
                const ctx = canvas.getContext('2d');
                if (!ctx) return;
                
                // Store original dimensions
                const width = canvas.width;
                const height = canvas.height;
                
                // Clear the canvas
                ctx.clearRect(0, 0, width, height);
                
                // Force a DOM reflow by changing dimensions
                canvas.width = 1;
                canvas.height = 1;
                canvas.width = width;
                canvas.height = height;
                
                // Reset all transforms
                ctx.setTransform(1, 0, 0, 1, 0, 0);
                
                // Add unique dataset attribute to force React to treat as new
                canvas.dataset.renderKey = `${Date.now()}-${Math.random()}`;
            };
            
            // Reset all relevant canvases
            setTimeout(() => {
                if (viewMode === 'carousel') {
                    const canvas = document.getElementById('carousel-canvas');
                    resetCanvas(canvas);
                    
                    // Redraw with the updated layout
                    if (canvas && updatedLayouts[editingIndex]) {
                        drawLayoutCanvas(canvas, updatedLayouts[editingIndex]);
                    }
                } else {
                    // In grid mode, reset and redraw all canvases
                    updatedLayouts.forEach((layout, idx) => {
                        const canvas = document.getElementById(`grid-canvas-${idx}`);
                        resetCanvas(canvas);
                        
                        if (canvas) {
                            drawLayoutCanvas(canvas, layout, idx);
                        }
                    });
                }
            }, 50);
        };
        
        // Execute canvas reset immediately and then several more times with delays
        forceCanvasReset();
        setTimeout(forceCanvasReset, 200);
        setTimeout(forceCanvasReset, 500);
    };

    // Handle cancel edit
    const handleCancelEdit = () => {
        // Exit edit mode without saving changes
        setIsEditing(false);
        setEditingLayout(null);
        setEditingIndex(null);
        
        // Redraw canvases to ensure they show the current state
        setTimeout(() => {
            setRedrawTrigger(Date.now());
            redrawAllCanvases();
        }, 100);
    };

    // Draw layouts on canvases whenever they change or view mode changes
    useEffect(() => {
        if (!processedLayouts || processedLayouts.length === 0 || isLoading) return;
        
        console.log("Drawing canvases due to layout/view update");
        
        // Wait for DOM to be ready
        const timer = setTimeout(() => {
            redrawAllCanvases();
        }, 200);
        
        return () => clearTimeout(timer);
    }, [processedLayouts, viewMode, currentIndex, redrawTrigger, isLoading]);

    // Update window.quiltGridData when quilt grid changes
    useEffect(() => {
        if (!quiltGrid || quiltGrid.length === 0) return;
        
        // Store quilt grid data for download function to access
        try {
            window.quiltGridData = JSON.parse(JSON.stringify(quiltGrid));
            sessionStorage.setItem('quiltGridData', JSON.stringify(quiltGrid));
        } catch (error) {
            console.error("Error storing quilt grid data:", error);
        }
    }, [quiltGrid]);

    // Load quilt dimensions when component mounts
    useEffect(() => {
        const loadQuiltDimensions = async () => {
            if (!currentUser?.uid || !db) return;
            
            try {
                const sessionRef = doc(db, 'sessions', currentUser.uid);
                const sessionSnap = await getDoc(sessionRef);
                
                if (sessionSnap.exists()) {
                    const sessionData = sessionSnap.data();
                    if (sessionData.quiltDimensions) {
                        // Store dimensions for canvas calculations
                        window.quiltDimensions = sessionData.quiltDimensions;
                        console.log('Loaded quilt dimensions for Step5:', sessionData.quiltDimensions);
                    }
                }
            } catch (error) {
                console.error('Error loading quilt dimensions:', error);
            }
        };
        
        loadQuiltDimensions();
    }, [currentUser, db]);

    // Helper function to draw a layout on a canvas
    const drawLayoutCanvas = (canvas, layout, index) => {
        if (!canvas || !layout) {
            console.warn("Missing canvas or layout in drawLayoutCanvas");
            return;
        }
        
        try {
            console.log(`Drawing layout on canvas: ${index !== undefined ? `grid-canvas-${index}` : 'carousel-canvas'}`);
            
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                console.error("Could not get canvas context");
                return;
            }
            
            // Get the layout-specific grid size
            const layoutGridSize = layout.original_grid_size || gridSize;
            if (!layoutGridSize) {
                console.error("No grid size available for drawing");
                return;
            }
            
            // Set canvas size using actual dimensions
            const { width, height } = getCanvasSize(layoutGridSize, window.quiltDimensions);
            canvas.width = width;
            canvas.height = height;
            
            // Create a position map from the original quilt grid with deep cloning to avoid reference issues
            const tilePositionMap = {};
            if (quiltGrid && Array.isArray(quiltGrid)) {
                quiltGrid.forEach(tile => {
                    if (tile && tile.position) {
                        const [row, col] = tile.position;
                        const key = `${row},${col}`;
                        // Use JSON parse/stringify for deep cloning to avoid reference issues
                        tilePositionMap[key] = JSON.parse(JSON.stringify(tile));
                    }
                });
            }
            
            // Check if layout has a matrix
            if (!layout.matrix) {
                console.error("No matrix found in layout");
                ctx.fillStyle = '#ffeeee';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.font = '14px Arial';
                ctx.fillStyle = '#cc0000';
                ctx.textAlign = 'center';
                ctx.fillText("Error: No layout data available", canvas.width/2, canvas.height/2);
                return;
            }
            
            // Create tile array from the matrix
            let drawableTiles = [];
            
            // FIXED: Better matrix processing with proper error handling
            if (Array.isArray(layout.matrix)) {
                console.log(`Processing 2D array matrix with ${layout.matrix.length} rows`);
                layout.matrix.forEach((row, rowIndex) => {
                    if (Array.isArray(row)) {
                        row.forEach((position, colIndex) => {
                            // Handle position format (string "row,col" or array [row,col])
                            let posKey;
                            if (typeof position === 'string') {
                                posKey = position;
                            } else if (Array.isArray(position) && position.length === 2) {
                                posKey = `${position[0]},${position[1]}`;
                            } else {
                                console.warn(`Invalid position format at [${rowIndex},${colIndex}]:`, position);
                                posKey = `${rowIndex},${colIndex}`; // Use current position as fallback
                            }
                            
                            // Get the original tile from the position map
                            const originalTile = tilePositionMap[posKey];
                            
                            if (originalTile) {
                                // Create completely new tile object with a unique key
                                drawableTiles.push({
                                    ...originalTile,
                                    position: [rowIndex, colIndex],
                                    uniqueKey: `${rowIndex}-${colIndex}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                                    url: originalTile.url,  // Explicitly include URL
                                    originalPosition: posKey // Track the original position
                                });
                            } else {
                                console.warn(`No tile found for position ${posKey} at [${rowIndex},${colIndex}]`);
                                // Add placeholder if original tile not found
                                drawableTiles.push({
                                    position: [rowIndex, colIndex],
                                    url: null,
                                    placeholder: true,
                                    uniqueKey: `placeholder-${rowIndex}-${colIndex}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
                                });
                            }
                        });
                    }
                });
            } 
            // FIXED: Handle object matrix with proper conversion
            else if (typeof layout.matrix === 'object' && !Array.isArray(layout.matrix)) {
                console.log("Matrix is in object format, converting to drawable tiles");
                const [cols, rows] = layoutGridSize.split('x').map(Number);
                
                // Create proper 2D array first
                const matrixArray = Array(rows).fill().map(() => Array(cols).fill(""));
                
                // Fill the array from object
                Object.entries(layout.matrix).forEach(([key, value]) => {
                    const [rowIndex, colIndex] = key.split('_').map(Number);
                    if (rowIndex >= 0 && rowIndex < rows && colIndex >= 0 && colIndex < cols) {
                        matrixArray[rowIndex][colIndex] = value;
                    }
                });
                
                // Now process the converted array
                matrixArray.forEach((row, rowIndex) => {
                    row.forEach((position, colIndex) => {
                        if (position) {
                            let posKey;
                            if (typeof position === 'string') {
                                posKey = position;
                            } else if (Array.isArray(position) && position.length === 2) {
                                posKey = `${position[0]},${position[1]}`;
                            } else {
                                posKey = `${rowIndex},${colIndex}`;
                            }
                            
                            const originalTile = tilePositionMap[posKey];
                            
                            if (originalTile) {
                                drawableTiles.push({
                                    ...originalTile,
                                    position: [rowIndex, colIndex],
                                    uniqueKey: `${rowIndex}-${colIndex}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                                    url: originalTile.url,
                                    originalPosition: posKey
                                });
                            } else {
                                console.warn(`No tile found for position ${posKey} at [${rowIndex},${colIndex}]`);
                            }
                        }
                    });
                });
                
                // Update the layout matrix to the converted array for future use
                layout.matrix = matrixArray;
            } else {
                console.error("Matrix format not recognized:", typeof layout.matrix, layout.matrix);
                ctx.fillStyle = '#fff8e8';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.font = '14px Arial';
                ctx.fillStyle = '#cc6600';
                ctx.textAlign = 'center';
                ctx.fillText("Error: Invalid matrix format", canvas.width/2, canvas.height/2);
                return;
            }
            
            // Log the drawable tiles for debugging
            console.log(`Prepared ${drawableTiles.length} drawable tiles for canvas`, 
                        drawableTiles.map(t => ({ 
                            position: t.position,
                            url: t.url ? t.url.substring(0, 20) + '...' : 'none',
                            hasOriginalTile: !!t.originalPosition
                        })));
            
            // Draw the matrix on the canvas
            if (drawableTiles.length > 0) {
                drawOnCanvas(
                    { current: canvas },
                    drawableTiles,
                    layoutGridSize,
                    logoSrc,
                    true // Use enhanced tile size
                );
            } else {
                console.error("No drawable tiles available");
                ctx.fillStyle = '#fff8e8';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.font = '14px Arial';
                ctx.fillStyle = '#cc6600';
                ctx.textAlign = 'center';
                ctx.fillText("Error: Could not load layout tiles", canvas.width/2, canvas.height/2);
            }
        } catch (error) {
            console.error("Error drawing layout canvas:", error);
            // Draw error state
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.fillStyle = '#ffeeee';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.font = '14px Arial';
                ctx.fillStyle = '#cc0000';
                ctx.textAlign = 'center';
                ctx.fillText(`Render Error: ${error.message}`, canvas.width/2, canvas.height/2);
            }
        }
    };

    // Utility functions for displaying layout info
    const getLayoutName = (index) => {
        if (!processedLayouts || !processedLayouts[index]) return `Layout ${index + 1}`;
        return processedLayouts[index].layout_name || 
               processedLayouts[index].title || 
               `Layout ${index + 1}`;
    };

    const getLayoutDescription = (index) => {
        if (!processedLayouts || !processedLayouts[index]) return 'Custom layout generated by AI';
        return processedLayouts[index].description || 'Custom layout generated by AI';
    };

    // Ensure current index is valid
    useEffect(() => {
        if (processedLayouts && processedLayouts.length > 0 && currentIndex >= processedLayouts.length) {
            setCurrentIndex(0);
        }
    }, [processedLayouts, currentIndex]);

    // Handle view mode change
    const handleViewModeChange = (newMode) => {
        setViewMode(newMode);
        setTimeout(() => {
            setRedrawTrigger(Date.now());
        }, 50);
    };

    // Handle share button click
    const handleSafeShareClick = (layout, layoutName) => {
        setLocalShowModal(true);
        setLocalShareLink("");
        setSharingLayoutIndex(currentIndex);
        
        try {
            // Get the canvas to share
            const canvasId = viewMode === 'carousel' ? 'carousel-canvas' : `grid-canvas-${currentIndex}`;
            const canvas = document.getElementById(canvasId);
            
            if (!canvas) {
                throw new Error("Canvas not found. Please try again.");
            }
            
            // Create a high-resolution canvas for sharing
            const tempCanvas = document.createElement('canvas');
            const ctx = tempCanvas.getContext('2d');
            
            // Get layout grid size
            const layoutGridSize = layout?.original_grid_size || gridSize;
            const [cols, rows] = layoutGridSize.split('x').map(Number);
            
            // Set canvas dimensions
            const tileSize = 150;
            const watermarkHeight = 50;
            
            tempCanvas.width = cols * tileSize;
            tempCanvas.height = (rows * tileSize) + watermarkHeight;
            
            // Fill white background
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
            
            // Create position map from quilt grid
            const tilePositionMap = {};
            if (quiltGrid && quiltGrid.length > 0) {
                quiltGrid.forEach(tile => {
                    if (tile && tile.position) {
                        const [row, col] = tile.position;
                        tilePositionMap[`${row},${col}`] = tile;
                    }
                });
            }
            
            // Load images
            const loadPromises = [];
            const tileImages = [];
            
            if (layout?.matrix && Array.isArray(layout.matrix) && Object.keys(tilePositionMap).length > 0) {
                // Process each tile in the layout
                layout.matrix.forEach((row, rowIndex) => {
                    row.forEach((position, colIndex) => {
                        let posKey;
                        if (typeof position === 'string') {
                            posKey = position;
                        } else if (Array.isArray(position) && position.length === 2) {
                            posKey = `${position[0]},${position[1]}`;
                        } else {
                            return;
                        }
                        
                        const originalTile = tilePositionMap[posKey];
                        if (originalTile && originalTile.url) {
                            // Load the image
                            const loadPromise = new Promise((resolve) => {
                                const img = new Image();
                                img.crossOrigin = "anonymous";
                                
                                img.onload = () => {
                                    tileImages.push({
                                        img: img,
                                        row: rowIndex,
                                        col: colIndex
                                    });
                                    resolve();
                                };
                                
                                img.onerror = () => {
                                    console.error(`Failed to load image for position ${posKey}`);
                                    resolve();
                                };
                                
                                img.src = originalTile.url;
                            });
                            
                            loadPromises.push(loadPromise);
                        }
                    });
                });
                
                // Wait for all images to load
                Promise.all(loadPromises)
                    .then(() => {
                        // Draw all images
                        tileImages.forEach(item => {
                            const x = item.col * tileSize;
                            const y = item.row * tileSize;
                            ctx.drawImage(item.img, x, y, tileSize, tileSize);
                            
                            // Add grid border
                            ctx.strokeStyle = '#eaeaea';
                            ctx.lineWidth = 1;
                            ctx.strokeRect(x, y, tileSize, tileSize);
                        });
                        
                        // Add separator line
                        ctx.strokeStyle = '#dddddd';
                        ctx.lineWidth = 1;
                        ctx.beginPath();
                        ctx.moveTo(0, rows * tileSize);
                        ctx.lineTo(tempCanvas.width, rows * tileSize);
                        ctx.stroke();
                        
                        // Add watermark text
                        ctx.font = 'bold 16px Arial';
                        ctx.fillStyle = '#555555';
                        ctx.textAlign = 'right';
                        const textY = rows * tileSize + (watermarkHeight / 2) + 4;
                        ctx.fillText('Generated with NOSTAI', tempCanvas.width - 50, textY);
                        
                        // Add logo if provided
                        if (logoSrc) {
                            const logoImg = new Image();
                            logoImg.crossOrigin = "anonymous";
                            logoImg.src = logoSrc;
                            
                            logoImg.onload = () => {
                                const logoSize = 24;
                                const logoX = tempCanvas.width - 15 - logoSize - 5;
                                const logoY = textY - (logoSize / 2);
                                ctx.drawImage(logoImg, logoX, logoY - 8, logoSize, logoSize);
                                uploadAndShare(tempCanvas);
                            };
                            
                            logoImg.onerror = () => {
                                uploadAndShare(tempCanvas);
                            };
                        } else {
                            uploadAndShare(tempCanvas);
                        }
                    })
                    .catch(error => {
                        console.error("Error during image loading:", error);
                        setLocalShareLink("Error creating share link: " + error.message);
                    });
            } else {
                setLocalShareLink("Error: Could not generate layout for sharing");
            }
        } catch (error) {
            console.error("Error preparing layout for sharing:", error);
            setLocalShareLink("Error creating share link: " + error.message);
        }
        
        // Function to upload and create share link
        function uploadAndShare(canvas) {
            if (currentUser && storage && db) {
                try {
                    canvas.toBlob(async (blob) => {
                        try {
                            const designId = `${currentUser.uid}-${Date.now()}`;
                            const storageRef = ref(storage, `user_quilts/${currentUser.uid}/${designId}.jpg`);
                            await uploadBytes(storageRef, blob);
                            const publicUrl = await getDownloadURL(storageRef);
                            
                            await setDoc(doc(db, "shared_quilts", designId), {
                                quiltImageUrl: publicUrl,
                                userId: currentUser.uid,
                                createdAt: new Date(),
                                layoutName: layoutName || "AI Layout",
                                isAILayout: true
                            });
                            
                            const shareableLink = `https://nostai.com/c/${designId}`;
                            setLocalShareLink(shareableLink);
                        } catch (error) {
                            setLocalShareLink("Error sharing: " + error.message);
                        }
                    }, 'image/jpeg', 0.85);
                } catch (error) {
                    setLocalShareLink("Error creating share image: " + error.message);
                }
            } else {
                setLocalShareLink("Error: Missing authentication or storage access");
            }
        }
    };

    // Share modal component
    const LocalShareModal = () => {
        if (!localShowModal) return null;
        
        const handleCloseModal = () => {
            setLocalShowModal(false);
            setModalClosedTimestamp(Date.now());
            redrawAllCanvases();
        };
        
        let layoutName = "AI Layout";
        if (sharingLayoutIndex !== null && processedLayouts && processedLayouts[sharingLayoutIndex]) {
            layoutName = processedLayouts[sharingLayoutIndex].layout_name || 
                         processedLayouts[sharingLayoutIndex].title || 
                         `Layout ${sharingLayoutIndex + 1}`;
        }
        
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-4 sm:p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold" style={{ fontFamily: "Playfair Display" }}>
                            Share "{layoutName}"
                        </h3>
                        <button 
                            onClick={handleCloseModal}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                    
                    {!localShareLink ? (
                        <div className="flex justify-center items-center py-6">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                            <p className="ml-3 text-gray-600">Creating share link...</p>
                        </div>
                    ) : localShareLink.startsWith("Error") ? (
                        <div className="bg-red-50 p-3 rounded text-red-700 mb-4">
                            {localShareLink}
                        </div>
                    ) : (
                        <>
                            <p className="mb-3 text-gray-600 text-sm">
                                Share this link with others to view your AI layout:
                            </p>
                            <div className="flex items-center mb-4">
                                <input 
                                    type="text" 
                                    value={localShareLink} 
                                    readOnly 
                                    className="flex-1 p-2 border rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button 
                                    onClick={() => {
                                        navigator.clipboard.writeText(localShareLink);
                                        alert("Link copied to clipboard!");
                                    }}
                                    className="bg-[#5B7A8C] hover:bg-[#496779] text-white px-3 py-2 rounded-r-md"
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </button>
                            </div>
                        </>
                    )}
                    
                    <div className="flex justify-end">
                        <button 
                            onClick={handleCloseModal}
                            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-gray-700"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Loading state UI
    if (isLoading) {
        return (
            <div className="bg-white rounded-lg p-3 sm:p-6 shadow-md flex flex-col items-center justify-center min-h-[300px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9CAF88]"></div>
                <p className="mt-4 text-gray-600">Loading AI layouts...</p>
            </div>
        );
    }

    // Error state UI
    if (loadError && (!processedLayouts || processedLayouts.length === 0)) {
        return (
            <div className="bg-white rounded-lg p-3 sm:p-6 shadow-md">
                <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-red-600" style={{ fontFamily: "Playfair Display" }}>
                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                    Error Loading Layouts
                </h2>
                
                <div className="bg-red-50 p-3 sm:p-4 rounded-lg border-l-4 border-red-500 mb-4 sm:mb-6">
                    <p className="text-red-800 text-sm sm:text-base">{loadError}</p>
                </div>
                
                <div className="flex justify-between mt-4 sm:mt-6">
                    <button
                        onClick={prevStep}
                        className="w-full sm:w-auto px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg flex items-center justify-center"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Back to Basic Layout
                    </button>
                </div>
            </div>
        );
    }

    // No layouts UI
    if (!processedLayouts || processedLayouts.length === 0) {
        return (
            <div className="bg-white rounded-lg p-3 sm:p-6 shadow-md">
                <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-indigo-800" style={{ fontFamily: "Playfair Display" }}>
                    <FontAwesomeIcon icon={faMagic} className="mr-2" />
                    AI-Powered Layout Suggestions
                </h2>
                
                <div className="bg-yellow-50 p-3 sm:p-4 rounded-lg border-l-4 border-yellow-500 mb-4 sm:mb-6">
                    <p className="text-yellow-800 text-sm sm:text-base">
                        No AI layouts available yet. Please go back to the previous step and click "Generate AI Layout Suggestions".
                    </p>
                </div>
                
                <div className="flex justify-between mt-4 sm:mt-6">
                    <button
                        onClick={prevStep}
                        className="w-full sm:w-auto px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg flex items-center justify-center"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Back to Basic Layout
                    </button>
                </div>
            </div>
        );
    }

    // Editing mode UI
    if (isEditing) {
        return (
            <div className="bg-white rounded-lg p-3 sm:p-6 shadow-md">
                <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#9CAF88]" style={{ fontFamily: "Playfair Display" }}>
                    <FontAwesomeIcon icon={faEdit} className="mr-2" />
                    Editing AI Layout
                </h2>
                
                <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
                    Rearrange the tiles by clicking pairs of tiles to swap their positions.
                    Click "Save Changes" when you're done to save your edits.
                </p>
                
                <QuiltEditor
                    quiltGrid={layoutTiles}
                    gridSize={editingLayout?.original_grid_size || gridSize}
                    onSave={handleSaveChanges}
                    onCancel={handleCancelEdit}
                    saveButtonText="Save Changes"
                />
            </div>
        );
    }

    // Main UI with layouts display
    return (
        <div className="bg-white rounded-lg p-3 sm:p-6 shadow-md">
            <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#9CAF88]" style={{ fontFamily: "Playfair Display" }}>
                <FontAwesomeIcon icon={faMagic} className="mr-2" />
                AI-Powered Layout Designs
            </h2>
            
            <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
                Our AI has analyzed your clothing items and generated custom layout options based on color, patterns, and themes.
                You can edit and rearrange the tiles to better fit your preferences!
            </p>
            
            {/* Auto-save status indicators */}
            {isSaving && (
                <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500 mb-4 flex items-center">
                    <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500 mr-2" />
                    <p className="text-blue-800 text-sm">Saving your changes...</p>
                </div>
            )}
            
            {/* Save success message */}
            {saveSuccess && (
                <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500 mb-4 flex items-center">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2" />
                    <p className="text-green-800 text-sm">Changes saved successfully!</p>
                </div>
            )}
            
            {/* Save error message */}
            {saveError && (
                <div className="bg-red-50 p-3 rounded-lg border-l-4 border-red-500 mb-4">
                    <p className="text-red-800 text-sm">{saveError}</p>
                </div>
            )}
            
            {/* View mode toggles */}
            <div className="flex justify-end mb-4 sm:mb-6">
                <div className="inline-flex bg-gray-100 rounded-lg p-1">
                    <button
                        onClick={() => handleViewModeChange('carousel')}
                        className={`px-3 sm:px-4 py-2 rounded-md flex items-center text-sm sm:text-base ${
                            viewMode === 'carousel' 
                                ? 'bg-[#9CAF88] text-white' 
                                : 'text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        <FontAwesomeIcon icon={faCaretRight} className="mr-2" />
                        <span className="hidden sm:inline">Carousel</span>
                        <span className="sm:hidden">Single</span>
                    </button>
                    <button
                        onClick={() => handleViewModeChange('grid')}
                        className={`px-3 sm:px-4 py-2 rounded-md flex items-center text-sm sm:text-base ${
                            viewMode === 'grid' 
                                ? 'bg-[#9CAF88] text-white' 
                                : 'text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        <FontAwesomeIcon icon={faThLarge} className="mr-2" />
                        <span className="hidden sm:inline">View All</span>
                        <span className="sm:hidden">All</span>
                    </button>
                </div>
            </div>
            
            {/* Carousel view */}
            {viewMode === 'carousel' && processedLayouts.length > 0 && (
                <div className="mb-4 sm:mb-6">
                    <div className="relative flex flex-col items-center">
                        {/* Canvas with navigation arrows */}
                        <div className="relative w-full max-w-4xl flex items-center justify-center mb-4 sm:mb-6">
                            <button
                                onClick={goToPrevLayout}
                                className="absolute left-0 z-10 bg-white bg-opacity-70 hover:bg-opacity-100 rounded-full p-2 sm:p-3 shadow-md -ml-2 sm:-ml-4"
                                aria-label="Previous layout"
                            >
                                <FontAwesomeIcon icon={faChevronLeft} className="text-indigo-800" />
                            </button>
                            
                            <div className="flex justify-center items-center p-2 sm:p-3 bg-gray-50 rounded-lg border w-full">
                                <div className="w-full flex justify-center items-center overflow-hidden">
                                    <canvas 
                                        id="carousel-canvas"
                                        className="max-w-full h-auto object-contain border rounded-lg shadow-inner"
                                        style={{ maxHeight: 'calc(90vh - 150px)' }} // Limit height on laptop screens
                                    />
                                </div>
                            </div>
                            
                            <button
                                onClick={goToNextLayout}
                                className="absolute right-0 z-10 bg-white bg-opacity-70 hover:bg-opacity-100 rounded-full p-2 sm:p-3 shadow-md -mr-2 sm:-mr-4"
                                aria-label="Next layout"
                            >
                                <FontAwesomeIcon icon={faChevronRight} className="text-indigo-800" />
                            </button>
                        </div>
                        
                        {/* Action buttons */}
                        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-4">
                            <button
                                onClick={() => handleEditClick(processedLayouts[currentIndex], currentIndex)}
                                className="bg-[#8B6F47] hover:bg-[#7A6040] text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg flex items-center text-sm sm:text-base"
                                disabled={!processedLayouts[currentIndex]}
                            >
                                <FontAwesomeIcon icon={faEdit} className="mr-1 sm:mr-2" />
                                <span className="hidden xs:inline">Edit Layout</span>
                                <span className="xs:hidden">Edit</span>
                            </button>
                            
                            <button
                                onClick={() => {
                                    window.quiltGridData = JSON.parse(JSON.stringify(quiltGrid));
                                    
                                    // Pass the actual dimensions from Step5's state
                                    const actualDimensions = window.quiltDimensions || {
                                        tileWidthPixels: 300,
                                        tileHeightPixels: 300,
                                        rows: 6,
                                        columns: 6
                                    };
                                    
                                    handleDownloadClick(
                                        null, 
                                        gridSize, 
                                        currentUser, 
                                        storage, 
                                        processedLayouts[currentIndex]?.matrix, 
                                        getLayoutName(currentIndex),
                                        logoSrc,
                                        actualDimensions // Pass the dimensions
                                    );
                                }}
                                className="bg-[#2D8B47] hover:bg-[#256E39] text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg flex items-center text-sm sm:text-base"
                                disabled={!processedLayouts[currentIndex]}
                            >
                                <FontAwesomeIcon icon={faDownload} className="mr-1 sm:mr-2" />
                                <span>Download</span>
                            </button>
                            
                            <button
                                onClick={() => handleSafeShareClick(
                                    processedLayouts[currentIndex],
                                    getLayoutName(currentIndex)
                                )}
                                className="bg-[#5B7A8C] hover:bg-[#496779] text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg flex items-center text-sm sm:text-base"
                                disabled={!processedLayouts[currentIndex]}
                            >
                                <FontAwesomeIcon icon={faShareAlt} className="mr-1 sm:mr-2" />
                                <span>Share</span>
                            </button>
                            
                            {/* Customize button */}
                            <button
                                onClick={() => {
                                    // Store the quilt grid data in session storage for Step6 to access
                                    if (quiltGrid && quiltGrid.length > 0) {
                                        sessionStorage.setItem('quiltGridData', JSON.stringify(quiltGrid));
                                    }
                                    
                                    // Pass additional data to the selected layout
                                    const layoutWithData = {
                                        ...processedLayouts[currentIndex],
                                        quiltGrid: quiltGrid, // Include quilt grid for rendering
                                        original_grid_size: gridSize, // Ensure grid size is available
                                    };
                                    
                                    onSelectLayout(layoutWithData);
                                }}
                                className="bg-[#9CAF88] hover:bg-[#8A9B79] text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg flex items-center text-sm sm:text-base"
                                disabled={!processedLayouts[currentIndex]}
                            >
                                <FontAwesomeIcon icon={faMagic} className="mr-1 sm:mr-2" />
                                <span>Customize</span>
                            </button>
                        </div>
                        
                        {/* Layout info */}
                        <div className="text-center mb-4 sm:mb-6 px-2">
                            <h3 className="text-lg sm:text-xl font-semibold text-[#9CAF88]">
                                {getLayoutName(currentIndex)}
                            </h3>
                            <p className="text-gray-600 italic mt-1 text-sm sm:text-base">
                                {getLayoutDescription(currentIndex)}
                            </p>
                        </div>
                        
                        {/* Pagination indicators */}
                        <div className="flex justify-center space-x-1">
                            {processedLayouts.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentIndex(index)}
                                    className={`h-2 rounded-full transition-all ${
                                        index === currentIndex ? 'w-6 sm:w-8 bg-[#9CAF88]' : 'w-2 bg-gray-300'
                                    }`}
                                    aria-label={`Go to layout ${index + 1}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}
            
            {/* Grid view */}
            {viewMode === 'grid' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                    {processedLayouts.map((layout, index) => {
                        const layoutName = layout.layout_name || layout.title || `Layout ${index + 1}`;
                        const description = layout.description || 'Custom layout generated by AI';
                        const layoutGridSize = layout.original_grid_size || gridSize;
                        
                        return (
                            <div key={`layout-${index}-${redrawTrigger}`} className="border rounded-lg overflow-hidden">
                                <div className="p-3 sm:p-4">
                                    {/* Layout canvas */}
                                    <div className="bg-gray-50 p-2 sm:p-3 border rounded mb-3 sm:mb-4">
                                        <div className="flex justify-center items-center">
                                            <canvas 
                                                id={`grid-canvas-${index}`}
                                                className="max-w-full h-auto border rounded-lg shadow-inner"
                                                key={`grid-canvas-${index}-${redrawTrigger}`}
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Action buttons */}
                                    <div className="flex flex-wrap justify-center gap-2 mt-3">
                                        <button
                                            onClick={() => handleEditClick(layout, index)}
                                            className="bg-[#8B6F47] hover:bg-[#7A6040] text-white py-1 px-2 sm:px-3 rounded flex items-center text-xs"
                                        >
                                            <FontAwesomeIcon icon={faEdit} className="mr-1" />
                                            <span>Edit</span>
                                        </button>
                                        
                                        <button
                                            onClick={() => {
                                                window.quiltGridData = JSON.parse(JSON.stringify(quiltGrid));
                                                
                                                // Pass the actual dimensions for grid downloads too
                                                const actualDimensions = window.quiltDimensions || {
                                                    tileWidthPixels: 300,
                                                    tileHeightPixels: 300,
                                                    rows: 6,
                                                    columns: 6
                                                };
                                                
                                                handleDownloadClick(
                                                    null,
                                                    layoutGridSize,
                                                    currentUser, 
                                                    storage,
                                                    layout.matrix,
                                                    layoutName,
                                                    logoSrc,
                                                    actualDimensions // Pass the dimensions
                                                );
                                            }}
                                            className="bg-[#2D8B47] hover:bg-[#256E39] text-white py-1 px-2 sm:px-3 rounded flex items-center text-xs"
                                        >
                                            <FontAwesomeIcon icon={faDownload} className="mr-1" />
                                            <span>Download</span>
                                        </button>
                                        
                                        <button
                                            onClick={() => handleSafeShareClick(layout, layoutName)}
                                            className="bg-[#5B7A8C] hover:bg-[#496779] text-white py-1 px-2 sm:px-3 rounded flex items-center text-xs"
                                        >
                                            <FontAwesomeIcon icon={faShareAlt} className="mr-1" />
                                            <span>Share</span>
                                        </button>
                                        
                                        {/* Customize button */}
                                        <button
                                            onClick={() => {
                                                // Store quilt grid for the next component
                                                if (quiltGrid && quiltGrid.length > 0) {
                                                    sessionStorage.setItem('quiltGridData', JSON.stringify(quiltGrid));
                                                }
                                                
                                                // Create enriched layout object
                                                const layoutWithData = {
                                                    ...layout,
                                                    quiltGrid: quiltGrid,
                                                    original_grid_size: layout.original_grid_size || gridSize,
                                                };
                                                
                                                onSelectLayout(layoutWithData);
                                            }}
                                            className="bg-[#9CAF88] hover:bg-[#8A9B79] text-white py-1 px-2 sm:px-3 rounded flex items-center text-xs"
                                        >
                                            <FontAwesomeIcon icon={faMagic} className="mr-1" />
                                            <span>Customize</span>
                                        </button>
                                    </div>
                                    
                                    {/* Layout info */}
                                    <div className="text-center border-t pt-2 sm:pt-3">
                                        <h3 className="font-bold text-[#9CAF88] mb-1 sm:mb-2 text-sm sm:text-base">
                                            {layoutName}
                                        </h3>
                                        <p className="text-gray-600 italic text-xs sm:text-sm">
                                            {description}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            
            {/* Navigation buttons */}
            <div className="flex justify-between mt-4 sm:mt-6">
                <button
                    onClick={() => {
                        // If there are ongoing operations, warn user
                        if (isSaving) {
                            if (window.confirm("Changes are still being saved. Are you sure you want to leave?")) {
                                sessionStorage.setItem("navigateToStep3", "true");
                                sessionStorage.setItem("explicitNavigation", "true");
                                prevStep();
                            }
                        } else {
                            // No ongoing operations, navigate directly
                            sessionStorage.setItem("navigateToStep3", "true");
                            sessionStorage.setItem("explicitNavigation", "true");
                            prevStep();
                        }
                    }}
                    className="w-full sm:w-auto px-4 py-2 bg-[#B8A99A] hover:bg-[#A69688] text-white rounded-lg flex items-center justify-center"
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Back to Basic Layout
                </button>
                
                {/* Save button removed - auto-save is now implemented */}
            </div>
            
            {/* Share modal */}
            {localShowModal && <LocalShareModal />}
        </div>
    );
};

export default Step5;